import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  PriorityHigh as ImportantIcon,
  Info as InfoIcon,
  Campaign as AdsIcon,
} from '@mui/icons-material';
import { emailsApi } from '../services/api';
import type { Email } from '../types/api';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const EmailCategoryWidget: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    loadEmails();
  }, []);

  const loadEmails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await emailsApi.getAll({ take: 50 });
      setEmails(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der E-Mails:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der E-Mails');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleEmailClick = (email: Email) => {
    navigate(`/emails?id=${email.emailID}`);
  };

  // Kategorisierte E-Mails (ohne beantwortete E-Mails)
  const categorizedEmails = {
    Wichtig: emails.filter((e) => e.kategorie === 'Wichtig' && !e.beantwortet),
    ZurKenntnis: emails.filter((e) => e.kategorie === 'ZurKenntnis' && !e.beantwortet),
    Werbung: emails.filter((e) => e.kategorie === 'Werbung' && !e.beantwortet),
  };

  // Prüfen ob überhaupt analysierte E-Mails vorhanden sind
  const hasAnalyzedEmails = emails.some((e) => e.analysiert);

  // Hilfsfunktion zum Bereinigen und Formatieren von Text (insbesondere KI-Zusammenfassungen)
  const cleanText = (text: string | undefined): string => {
    if (!text) return '';

    let cleaned = text;

    // Entferne JSON-Formatierungen und Escape-Zeichen
    cleaned = cleaned
      .replace(/\\n/g, ' ')           // Escape-Sequenzen für Zeilenumbrüche
      .replace(/\\r/g, ' ')
      .replace(/\\t/g, ' ')           // Tabs
      .replace(/\\/g, '')             // Backslashes
      .replace(/["{}[\]]/g, '')       // JSON-Zeichen
      .replace(/^["']|["']$/g, '');   // Führende/Nachfolgende Anführungszeichen

    // Entferne tatsächliche Zeilenumbrüche und normalisiere Whitespace
    cleaned = cleaned
      .replace(/\r\n/g, ' ')          // Windows Zeilenumbrüche
      .replace(/\n/g, ' ')            // Unix Zeilenumbrüche
      .replace(/\r/g, ' ')            // Mac Zeilenumbrüche
      .replace(/\s+/g, ' ')           // Mehrfache Leerzeichen zu einem
      .trim();

    // Entferne gängige KI-Präfixe falls vorhanden
    const prefixes = [
      'Zusammenfassung:',
      'Summary:',
      'Inhalt:',
      'Text:',
      'zusammenfassung:',
    ];

    for (const prefix of prefixes) {
      if (cleaned.startsWith(prefix)) {
        cleaned = cleaned.substring(prefix.length).trim();
      }
    }

    return cleaned;
  };

  const EmailList: React.FC<{ emails: Email[] }> = ({ emails }) => {
    if (emails.length === 0) {
      return (
        <Typography color="textSecondary" sx={{ p: 2 }}>
          Keine E-Mails in dieser Kategorie
        </Typography>
      );
    }

    return (
      <List>
        {emails.slice(0, 5).map((email) => (
          <ListItem key={email.emailID} disablePadding divider>
            <ListItemButton
              onClick={() => handleEmailClick(email)}
              sx={{
                py: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: email.gelesen ? 'normal' : 'bold',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {email.betreffText}
                    </Typography>
                    {!email.gelesen && (
                      <Chip label="Neu" size="small" color="primary" />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="body2"
                      color="textPrimary"
                      sx={{ mb: 0.5 }}
                    >
                      {email.absenderAnzeige || email.absenderEmail}
                    </Typography>

                    {email.zusammenfassung && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.5,
                          mb: 0.5,
                        }}
                      >
                        {cleanText(email.zusammenfassung)}
                      </Typography>
                    )}
                    {!email.zusammenfassung && email.textInhalt && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.5,
                          mb: 0.5,
                        }}
                      >
                        {cleanText(email.textInhalt)}
                      </Typography>
                    )}

                    <Typography variant="caption" color="textSecondary">
                      {new Date(email.empfangenAm).toLocaleString('de-DE')}
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        E-Mail-Kategorien (KI-Analyse)
      </Typography>

      {!hasAnalyzedEmails && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Noch keine analysierten E-Mails vorhanden. Die KI-Analyse erfolgt automatisch beim
          Abrufen neuer E-Mails. Bitte rufen Sie E-Mails ab, um die automatische Kategorisierung
          zu nutzen.
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab
            icon={<ImportantIcon />}
            iconPosition="start"
            label={
              <Badge badgeContent={categorizedEmails.Wichtig.length} color="error">
                <Box sx={{ ml: 1 }}>Wichtig</Box>
              </Badge>
            }
          />
          <Tab
            icon={<InfoIcon />}
            iconPosition="start"
            label={
              <Badge badgeContent={categorizedEmails.ZurKenntnis.length} color="info">
                <Box sx={{ ml: 1 }}>Zur Kenntnis</Box>
              </Badge>
            }
          />
          <Tab
            icon={<AdsIcon />}
            iconPosition="start"
            label={
              <Badge badgeContent={categorizedEmails.Werbung.length} color="default">
                <Box sx={{ ml: 1 }}>Werbung</Box>
              </Badge>
            }
          />
        </Tabs>
      </Box>

      <TabPanel value={selectedTab} index={0}>
        <EmailList emails={categorizedEmails.Wichtig} />
      </TabPanel>
      <TabPanel value={selectedTab} index={1}>
        <EmailList emails={categorizedEmails.ZurKenntnis} />
      </TabPanel>
      <TabPanel value={selectedTab} index={2}>
        <EmailList emails={categorizedEmails.Werbung} />
      </TabPanel>
    </Paper>
  );
};

export default EmailCategoryWidget;
